/**
 * Modal/Dialog components with StyleX styling.
 */
import * as stylex from "@stylexjs/stylex";

import { colors, fontSize, radius, space, zIndex } from "../tokens.stylex";

const styles = stylex.create({
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: zIndex.modalBackdrop,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  container: {
    width: "100%",
    maxWidth: 448,
    borderRadius: radius.lg,
    backgroundColor: colors.card,
    padding: space.lg,
    zIndex: zIndex.modal,
  },
  containerAlt: {
    backgroundColor: colors.backgroundSubtle,
  },
  header: {
    marginBottom: space.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  close: {
    color: colors.foregroundMuted,
    backgroundColor: colors.transparent,
    borderWidth: 0,
    borderStyle: "none",
    cursor: "pointer",
    fontSize: fontSize.xl,
    ":hover": {
      color: colors.foreground,
    },
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: space.md,
    paddingTop: space.md,
  },
});

// Export styles for direct use with stylex.props()
export const modal = styles;

interface ModalOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  xstyle?: stylex.StyleXStyles;
}

function ModalOverlay({ xstyle, ...props }: ModalOverlayProps) {
  return <div {...stylex.props(styles.overlay, xstyle)} {...props} />;
}

interface ModalContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "alt";
  xstyle?: stylex.StyleXStyles;
}

function ModalContainer({
  variant = "default",
  xstyle,
  ...props
}: ModalContainerProps) {
  return (
    <div
      {...stylex.props(
        styles.container,
        variant === "alt" && styles.containerAlt,
        xstyle
      )}
      {...props}
    />
  );
}

interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  xstyle?: stylex.StyleXStyles;
}

function ModalHeader({ xstyle, ...props }: ModalHeaderProps) {
  return <div {...stylex.props(styles.header, xstyle)} {...props} />;
}

interface ModalCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  xstyle?: stylex.StyleXStyles;
}

function ModalClose({ xstyle, ...props }: ModalCloseProps) {
  return <button {...stylex.props(styles.close, xstyle)} {...props} />;
}

interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  xstyle?: stylex.StyleXStyles;
}

function ModalFooter({ xstyle, ...props }: ModalFooterProps) {
  return <div {...stylex.props(styles.footer, xstyle)} {...props} />;
}

export {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalClose,
  ModalFooter,
  type ModalOverlayProps,
  type ModalContainerProps,
  type ModalHeaderProps,
  type ModalCloseProps,
  type ModalFooterProps,
};
